/**
 * Share Image Generator
 *
 * Generates shareable images for social media using Canvas API.
 * Creates two formats:
 * - Mobile: 9:16 vertical (Instagram Stories, TikTok)
 * - Desktop: 1:1 square (Twitter, Facebook)
 */

/**
 * Load an image and return as HTMLImageElement
 */
const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Enable CORS for external images
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Wrap text to fit within a specified width
 */
const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = ctx.measureText(currentLine + ' ' + word).width;
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

/**
 * Generate mobile share image (9:16 story format)
 * Option A: Smaller card + full summary below on dark background
 */
export const generateMobileShareImage = async (cardData) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // 9:16 aspect ratio (Instagram Stories / TikTok)
  canvas.width = 1080;
  canvas.height = 1920;

  // Dark background
  ctx.fillStyle = '#2C2C2C';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Set default text alignment to center
  ctx.textAlign = 'center';

  try {
    // Load Jaimee logo (cream version) - larger size
    const logo = await loadImage('/assets/images/jaimee-logo-cream.svg');
    const logoHeight = 70;
    const logoWidth = (logo.width / logo.height) * logoHeight;
    ctx.drawImage(logo, (canvas.width - logoWidth) / 2, 50, logoWidth, logoHeight);
  } catch (error) {
    console.error('Error loading logo:', error);
  }

  // Load and draw tarot card image (larger for more impact)
  try {
    const cardImage = await loadImage(cardData.cardImage);
    const cardWidth = 650; // Larger card for more visual impact
    const cardHeight = (cardImage.height / cardImage.width) * cardWidth;
    const cardX = (canvas.width - cardWidth) / 2;
    const cardY = 170; // Increased from 140 for more padding below logo

    // Draw card with shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 30;
    ctx.shadowOffsetY = 10;
    ctx.drawImage(cardImage, cardX, cardY, cardWidth, cardHeight);
    ctx.shadowColor = 'transparent';

    // Draw scroll indicator (vertical line + dot)
    const scrollIndicatorY = cardY + cardHeight + 25;
    const scrollIndicatorHeight = 40;

    // Vertical line
    ctx.fillStyle = 'rgba(229, 223, 208, 0.3)'; // Light cream for dark background
    ctx.fillRect(canvas.width / 2 - 0.5, scrollIndicatorY, 1, scrollIndicatorHeight);

    // Dot at top
    ctx.beginPath();
    ctx.arc(canvas.width / 2, scrollIndicatorY + 4, 4, 0, Math.PI * 2);
    ctx.fillStyle = '#E5DFD0';
    ctx.fill();

    // Draw summary - use summary_short if available, otherwise truncate full summary
    const summaryShort = cardData.backContent?.summary_short;
    const summaryFull = cardData.backContent?.summary || '';
    // Prefer summary_short from Firebase, fallback to truncated full summary
    const summaryText = summaryShort || (summaryFull.substring(0, 300) + (summaryFull.length > 300 ? '...' : ''));
    ctx.font = '36px Manrope, sans-serif'; // Increased from 32px
    ctx.fillStyle = '#E5DFD0'; // Light cream for body text
    const summaryLines = wrapText(ctx, summaryText, 920);
    const summaryY = scrollIndicatorY + scrollIndicatorHeight + 50;
    const lineHeight = 52; // Increased from 48px

    // If using summary_short, show all lines; otherwise limit to 5
    const maxLines = summaryShort ? summaryLines.length : 5;
    summaryLines.slice(0, maxLines).forEach((line, i) => {
      ctx.fillText(line, canvas.width / 2, summaryY + (i * lineHeight));
    });

    // CTA Section (bottom)
    const ctaY = canvas.height - 180;

    ctx.fillStyle = '#F5F1E8';
    ctx.font = 'bold 40px Manrope, sans-serif'; // Increased from 36px
    ctx.fillText("What's your personality type?", canvas.width / 2, ctaY);

    ctx.font = '34px Manrope, sans-serif'; // Increased from 30px
    ctx.fillStyle = '#FAA21B';
    ctx.fillText('Take the quiz → quiz.jaimee.ai', canvas.width / 2, ctaY + 55);

  } catch (error) {
    console.error('Error loading card image:', error);
    // Draw error state
    ctx.fillStyle = '#F5F1E8';
    ctx.font = '48px Manrope, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Discover your personality', canvas.width / 2, canvas.height / 2);
    ctx.fillText('at quiz.jaimee.ai', canvas.width / 2, canvas.height / 2 + 60);
  }

  // Convert canvas to Blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/png', 1.0);
  });
};

/**
 * Generate desktop share image (1:1 square format)
 * Dark background with card and summary snippet
 */
export const generateDesktopShareImage = async (cardData) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // 1:1 aspect ratio (Twitter, Facebook, LinkedIn)
  canvas.width = 1200;
  canvas.height = 1200;

  // Dark background
  ctx.fillStyle = '#2C2C2C';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Set default text alignment to center
  ctx.textAlign = 'center';

  try {
    // Load and draw tarot card image (centered)
    const cardImage = await loadImage(cardData.cardImage);
    const cardWidth = 520;
    const cardHeight = (cardImage.height / cardImage.width) * cardWidth;
    const cardX = (canvas.width - cardWidth) / 2;
    const cardY = 80;

    // Draw card with shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 30;
    ctx.shadowOffsetY = 10;
    ctx.drawImage(cardImage, cardX, cardY, cardWidth, cardHeight);
    ctx.shadowColor = 'transparent';

    // Draw scroll indicator (vertical line + dot)
    const scrollIndicatorY = cardY + cardHeight + 20;
    const scrollIndicatorHeight = 35;

    // Vertical line
    ctx.fillStyle = 'rgba(229, 223, 208, 0.3)'; // Light cream for dark background
    ctx.fillRect(canvas.width / 2 - 0.5, scrollIndicatorY, 1, scrollIndicatorHeight);

    // Dot at top
    ctx.beginPath();
    ctx.arc(canvas.width / 2, scrollIndicatorY + 4, 4, 0, Math.PI * 2);
    ctx.fillStyle = '#E5DFD0';
    ctx.fill();

    // Draw summary - use summary_short if available, otherwise full summary
    const summaryShort = cardData.backContent?.summary_short;
    const summaryText = summaryShort || cardData.backContent?.summary || '';
    ctx.font = '30px Manrope, sans-serif'; // Increased from 26px
    ctx.fillStyle = '#E5DFD0'; // Light cream
    const summaryLines = wrapText(ctx, summaryText, 1000);
    const summaryY = scrollIndicatorY + scrollIndicatorHeight + 35;
    const lineHeight = 42; // Increased from 36px

    summaryLines.forEach((line, i) => {
      ctx.fillText(line, canvas.width / 2, summaryY + (i * lineHeight));
    });

    // Bottom section with logo and CTA
    const bottomY = canvas.height - 80;

    // Draw Jaimee logo (centered above CTA) - larger size
    try {
      const logo = await loadImage('/assets/images/jaimee-logo-cream.svg');
      const logoHeight = 50; // Increased from 35px
      const logoWidth = (logo.width / logo.height) * logoHeight;
      ctx.drawImage(logo, (canvas.width - logoWidth) / 2, bottomY - 85, logoWidth, logoHeight);
    } catch (error) {
      console.error('Error loading logo:', error);
    }

    // Draw CTA text
    ctx.font = 'bold 32px Manrope, sans-serif'; // Increased from 28px
    ctx.fillStyle = '#FAA21B';
    ctx.fillText('Discover yours → quiz.jaimee.ai', canvas.width / 2, bottomY);

  } catch (error) {
    console.error('Error loading card image:', error);
    // Draw error state
    ctx.fillStyle = '#F5F1E8';
    ctx.font = '48px Manrope, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Discover your personality', canvas.width / 2, canvas.height / 2);
    ctx.fillText('at quiz.jaimee.ai', canvas.width / 2, canvas.height / 2 + 60);
  }

  // Convert canvas to Blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/png', 1.0);
  });
};

/**
 * Generate download image (longer version with full summary + app store icons)
 * Mobile-optimized vertical format
 */
export const generateDownloadImage = async (cardData) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Dynamic height based on summary length
  canvas.width = 1080;

  // Calculate approximate height needed for summary
  const summaryText = cardData.backContent?.summary || '';
  const estimatedLines = Math.ceil(summaryText.length / 50); // Rough estimate
  const baseHeight = 1920;
  const additionalHeight = Math.max(0, (estimatedLines - 8) * 42); // Extra space if summary is long
  canvas.height = baseHeight + additionalHeight;

  // Background - Dark (#2C2C2C)
  ctx.fillStyle = '#2C2C2C';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  try {
    // Load Jaimee logo (cream version)
    const logo = await loadImage('/assets/images/jaimee-logo-cream.svg');
    const logoHeight = 60;
    const logoWidth = (logo.width / logo.height) * logoHeight;
    ctx.drawImage(logo, (canvas.width - logoWidth) / 2, 80, logoWidth, logoHeight);
  } catch (error) {
    console.error('Error loading logo:', error);
  }

  // Load and draw tarot card image
  try {
    const cardImage = await loadImage(cardData.cardImage);
    const cardWidth = 720;
    const cardHeight = (cardImage.height / cardImage.width) * cardWidth;
    const cardX = (canvas.width - cardWidth) / 2;
    const cardY = 240;

    // Draw card with shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 30;
    ctx.shadowOffsetY = 10;
    ctx.drawImage(cardImage, cardX, cardY, cardWidth, cardHeight);
    ctx.shadowColor = 'transparent';

    // Draw scroll indicator under card (vertical line + dot)
    const scrollIndicator1Y = cardY + cardHeight + 30;
    const scrollIndicatorHeight = 50;

    // Vertical line
    ctx.fillStyle = 'rgba(229, 223, 208, 0.3)'; // Light cream for dark background
    ctx.fillRect(canvas.width / 2 - 0.5, scrollIndicator1Y, 1, scrollIndicatorHeight);

    // Dot at top
    ctx.beginPath();
    ctx.arc(canvas.width / 2, scrollIndicator1Y + 4, 4, 0, Math.PI * 2);
    ctx.fillStyle = '#E5DFD0';
    ctx.fill();

    // Draw FULL summary (not snippet)
    const summaryText = cardData.backContent?.summary || '';
    ctx.font = '28px Manrope, sans-serif';
    ctx.fillStyle = '#E5DFD0'; // Light cream for body text
    ctx.textAlign = 'center';
    const summaryLines = wrapText(ctx, summaryText, 900);
    const summaryY = scrollIndicator1Y + scrollIndicatorHeight + 40;
    const summaryLineHeight = 42;

    summaryLines.forEach((line, i) => {
      ctx.fillText(line, canvas.width / 2, summaryY + (i * summaryLineHeight));
    });

    // Draw scroll indicator under summary (vertical line + dot)
    const scrollIndicator2Y = summaryY + (summaryLines.length * summaryLineHeight) + 30;

    // Vertical line
    ctx.fillStyle = 'rgba(229, 223, 208, 0.3)';
    ctx.fillRect(canvas.width / 2 - 0.5, scrollIndicator2Y, 1, scrollIndicatorHeight);

    // Dot at top
    ctx.beginPath();
    ctx.arc(canvas.width / 2, scrollIndicator2Y + 4, 4, 0, Math.PI * 2);
    ctx.fillStyle = '#E5DFD0';
    ctx.fill();

    // App Store Icons Section
    const iconsY = scrollIndicator2Y + scrollIndicatorHeight + 80;

    // CTA heading
    ctx.fillStyle = '#F5F1E8';
    ctx.font = 'bold 44px Fraunces, serif';
    ctx.fillText("Want to talk more?", canvas.width / 2, iconsY);
    ctx.font = '36px Fraunces, serif';
    ctx.fillText("Download the free app", canvas.width / 2, iconsY + 60);

    // Load and draw app store badges (using PNG for reliability)
    try {
      const badgeY = iconsY + 120;
      const badgeHeight = 50;
      const badgeSpacing = 20;

      // Apple App Store badge
      const appleBadge = await loadImage('/assets/images/app-store-badge.png');
      const appleWidth = (appleBadge.width / appleBadge.height) * badgeHeight;

      // Google Play badge
      const googleBadge = await loadImage('/assets/images/google-play-badge.png');
      const googleWidth = (googleBadge.width / googleBadge.height) * badgeHeight;

      // Calculate positioning for centered badges
      const totalWidth = appleWidth + badgeSpacing + googleWidth;
      const startX = (canvas.width - totalWidth) / 2;

      // Draw badges
      ctx.drawImage(appleBadge, startX, badgeY, appleWidth, badgeHeight);
      ctx.drawImage(googleBadge, startX + appleWidth + badgeSpacing, badgeY, googleWidth, badgeHeight);
    } catch (error) {
      console.error('Error loading app store badges:', error);
      // Fallback text if badges fail to load
      ctx.font = '24px Manrope, sans-serif';
      ctx.fillStyle = '#999999';
      ctx.fillText("Available on iOS and Android", canvas.width / 2, iconsY + 140);
    }

  } catch (error) {
    console.error('Error loading card image:', error);
    ctx.fillStyle = '#F5F1E8';
    ctx.font = '48px Manrope, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Discover your personality', canvas.width / 2, canvas.height / 2);
    ctx.fillText('at quiz.jaimee.ai', canvas.width / 2, canvas.height / 2 + 60);
  }

  // Convert canvas to Blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.95);
  });
};

/**
 * Generate both mobile and desktop share images
 */
export const generateShareImages = async (cardData) => {
  console.log('🎨 Generating share images...');
  const startTime = performance.now();

  const [mobileBlob, desktopBlob] = await Promise.all([
    generateMobileShareImage(cardData),
    generateDesktopShareImage(cardData)
  ]);

  const endTime = performance.now();
  console.log(`✅ Share images generated in ${(endTime - startTime).toFixed(0)}ms`);

  return { mobileBlob, desktopBlob };
};

/**
 * Detect if user is on mobile device
 */
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         window.innerWidth < 768;
};

/**
 * Get shareable caption text
 */
export const getShareCaption = (cardTitle) => {
  return `I just discovered I'm a ${cardTitle}! 🃏\n\nWhat's your personality type? Take the quiz at https://quiz.jaimee.ai`;
};
